<template>
	<div>
		<PromoCampaignGauges :campaign="campaign" :compact="compact" />
		<PromoCampaignTiles v-if="!compact" :campaign="campaign" class="mt-4" />

		<div v-if="!compact && meansVisible" class="d-flex justify-end mt-4">
			<v-badge :content="campaign.applicationDomains.length" color="warning">
				<v-btn :prepend-icon="meansEditable ? 'sync_alt' : 'visibility'" :color="meansEditable ? 'success' : 'primary'" @click="meansOpen = true">{{ t(meansEditable ? 'PromoAssociateMeans' : 'PromoAssociatedMeans') }}</v-btn>
			</v-badge>
		</div>

		<div v-if="photoRules.visible" class="mt-4">
			<div class="text-body-small text-medium-emphasis mb-1">{{ t('PhotosStatement') }}</div>
			<PromoPhotoTiles :items="photoRules.items" :can-add="photoRules.canAdd" :can-edit="photoRules.canEdit" @upload="(replace) => ops.uploadPhoto({ campaignId: campaign.id, offerKey: null, isStatement: false, replace })" @delete="(photo) => ops.deletePhoto({ campaignId: campaign.id, offerKey: null, isStatement: false, photo })" />
		</div>

		<v-alert v-for="(error, index) in campaignErrors" :key="index" type="error" variant="tonal" density="compact" class="mt-3">{{ formatPromoMessage(error.display, t) }}</v-alert>

		<div v-if="campaign.promoLocal && canAddOffer" class="d-flex flex-wrap align-center ga-3 mt-4">
			<v-btn prepend-icon="add" color="primary" variant="flat" :block="compact" @click="ops.addLocalOffer(campaign.id)">{{ t('AddOfferPromoLocale') }}</v-btn>
			<span v-if="!compact" class="text-body-small text-medium-emphasis">{{ t('PromoLocalOfferHint') }}</span>
		</div>

		<PromoOfferPanel v-for="entry in offers" :key="entry.key" :campaign="campaign" :offer="entry.offer" :offer-key="entry.offerKey" class="mt-4" />
		<v-alert v-if="!offers.length && !campaign.promoLocal" type="info" variant="tonal" density="compact" class="mt-4">{{ t('PromoNoProductsPublished') }}</v-alert>

		<PromoMeansDialog v-if="!compact && meansVisible" v-model="meansOpen" :campaign="campaign" :editable="meansEditable" />
	</div>
</template>

<script setup>
import { computed, ref, toRef } from 'vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { usePromoCampaign } from '@/composables/usePromoCampaign';
import { nationalPhotoRules } from '@/utils/domain/promo/promoPhotos';
import { canAddLocalOffer } from '@/utils/domain/promo/promoLocalOffer';
import { formatPromoMessage } from '@/utils/domain/promo/promoValidation';
import PromoCampaignGauges from './PromoCampaignGauges.vue';
import PromoCampaignTiles from './PromoCampaignTiles.vue';
import PromoOfferPanel from './PromoOfferPanel.vue';
import PromoPhotoTiles from './PromoPhotoTiles.vue';
import PromoMeansDialog from './PromoMeansDialog.vue';

const props = defineProps({
	campaign: { type: Object, required: true },
});

const { snapshot, validation, compact, ops, t } = usePromoStep();
const { offers, meansEditable, meansVisible } = usePromoCampaign(toRef(props, 'campaign'));
const meansOpen = ref(false);

const photoRules = computed(() => (props.campaign.promoLocal ? { visible: false } : nationalPhotoRules(snapshot.value, props.campaign)));
const canAddOffer = computed(() => canAddLocalOffer(snapshot.value, props.campaign));
const campaignErrors = computed(() => (validation.value?.errors || []).filter((e) => e.level === 'campaign' && e.campaignId === props.campaign.id));
</script>
